import { useState, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

interface CoursedeePackage {
  id: string;
  name: string;
  version: string;
  price: number;
  purchaseDate: any;
  paymentId: string;
}

interface CoursedeeAccessResult {
  loading: boolean;
  hasAccess: boolean;
  hasV1Access: boolean;
  hasV2Access: boolean;
  packages: CoursedeePackage[];
  error: string | null;
}

const STORAGE_KEY = 'coursedee_access_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedAccess {
  hasV1Access: boolean;
  hasV2Access: boolean;
  timestamp: number;
  userId: string;
}

export const useCoursedeeAccess = (requiredVersion?: 'v1' | 'v2'): CoursedeeAccessResult => {
  const { user } = useAuthContext();
  
  // Initialize states with cached values if available
  const getCachedAccess = (): CachedAccess | null => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (!cached) return null;
      
      const data: CachedAccess = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid and for the same user
      if (data.userId === user?.uid && (now - data.timestamp) < CACHE_DURATION) {
        return data;
      }
      
      // Clear expired or invalid cache
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    } catch {
      return null;
    }
  };
  
  const cachedData = getCachedAccess();
  
  const [loading, setLoading] = useState(!cachedData);
  const [packages, setPackages] = useState<CoursedeePackage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasV1Access, setHasV1Access] = useState(cachedData?.hasV1Access || false);
  const [hasV2Access, setHasV2Access] = useState(cachedData?.hasV2Access || false);
  
  // Calculate hasAccess based on cached data or default to false
  const calculateAccess = (v1: boolean, v2: boolean): boolean => {
    if (requiredVersion === 'v1') return v1;
    if (requiredVersion === 'v2') return v2;
    return v1 || v2;
  };
  
  const [hasAccess, setHasAccess] = useState(
    cachedData ? calculateAccess(cachedData.hasV1Access, cachedData.hasV2Access) : false
  );

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        // ยังไม่มี user (auth กำลังโหลด) → คง loading=true ไว้ อย่าเพิ่งตั้ง false
        // กัน race ที่ guard เห็น accessLoading=false + hasAccess=false ชั่วขณะ แล้วเด้งออก (guard จัดการเคสไม่มี user เองที่หน้า login)
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get packages from user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        let userDataPackages: CoursedeePackage[] = [];

        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.coursedeePackages && Array.isArray(data.coursedeePackages)) {
            userDataPackages = data.coursedeePackages.map((pkg: any) => ({
              id: pkg.id,
              name: pkg.name,
              version: pkg.version,
              price: pkg.price,
              purchaseDate: pkg.purchaseDate,
              paymentId: pkg.paymentId
            }));
          }
        }

        // Also get packages from payments collection (Coursedee Cart purchases)
        const paymentsRef = collection(db, 'payments');

        // Query by userId
        const paymentsQueryByUserId = query(
          paymentsRef,
          where('userId', '==', user.uid),
          where('status', '==', 'verified')
        );
        const paymentsSnapshotByUserId = await getDocs(paymentsQueryByUserId);

        // Also query by email as fallback
        const paymentsQueryByEmail = query(
          paymentsRef,
          where('email', '==', user.email),
          where('status', '==', 'verified')
        );
        const paymentsSnapshotByEmail = await getDocs(paymentsQueryByEmail);

        const cartPackages: CoursedeePackage[] = [];
        const processedPaymentIds = new Set<string>();

        // Process payments from both queries
        [paymentsSnapshotByUserId, paymentsSnapshotByEmail].forEach((snapshot) => {
          snapshot.forEach((doc) => {
            // Skip if already processed
            if (processedPaymentIds.has(doc.id)) return;
            processedPaymentIds.add(doc.id);

            const data = doc.data();
            // Extract packages from cart items
            if (data.items && Array.isArray(data.items)) {
              data.items.forEach((item: any) => {
                cartPackages.push({
                  id: item.id || doc.id,
                  name: item.name || 'AIForYou Package',
                  version: item.version || 'v1',
                  price: item.price || 0,
                  purchaseDate: data.createdAt || data.paymentDate,
                  paymentId: doc.id
                });
              });
            }
          });
        });

        // Merge packages from user data and payments collection
        const allPackages = [...userDataPackages, ...cartPackages];
        
        // Remove duplicates based on id
        const uniquePackages = allPackages.filter((pkg, index, self) => 
          index === self.findIndex((p) => p.id === pkg.id)
        );

        setPackages(uniquePackages);
        
        // Debug: Log packages
        console.log('[useCoursedeeAccess] Packages loaded:', uniquePackages);
        console.log('[useCoursedeeAccess] Package versions:', uniquePackages.map(p => p.version));
        
        // Calculate access after packages are loaded
        const v1Access = uniquePackages.some(pkg => pkg.version.toLowerCase() === 'v1');
        const v2Access = uniquePackages.some(pkg => pkg.version.toLowerCase() === 'v2');
        
        console.log('[useCoursedeeAccess] v1Access:', v1Access, 'v2Access:', v2Access);
        console.log('[useCoursedeeAccess] requiredVersion:', requiredVersion);
        
        setHasV1Access(v1Access);
        setHasV2Access(v2Access);
        
        // Cache the access data in sessionStorage
        const cacheData: CachedAccess = {
          hasV1Access: v1Access,
          hasV2Access: v2Access,
          timestamp: Date.now(),
          userId: user.uid
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
        
        // Set hasAccess based on required version
        const accessGranted = calculateAccess(v1Access, v2Access);
        
        console.log('[useCoursedeeAccess] hasAccess will be set to:', accessGranted);
        setHasAccess(accessGranted);
        
        setLoading(false);
      } catch (err) {
        console.error('[useCoursedeeAccess] Error:', err);
        setError('เกิดข้อผิดพลาดในการตรวจสอบสิทธิ์');
        setLoading(false);
      }
    };

    // Only fetch if we don't have valid cached data
    if (!cachedData) {
      checkAccess();
    } else {
      console.log('[useCoursedeeAccess] Using cached access data');
    }
  }, [user, requiredVersion]);

  return {
    loading,
    hasAccess,
    hasV1Access,
    hasV2Access,
    packages,
    error
  };
};
